import React, { useState } from 'react';
import Navbar from '../../components/navbar/Navbar';
import Footer from '../../components/footer/Footer';
import Container from '../../components/Container';
import { ChevronDown, ChevronRight, Calendar, Users, Mail, Lightbulb, Clock, Shield, Heart, Sparkles, Handshake } from 'lucide-react';

const AccordionItem = ({ title, children, isOpen, onToggle }) => (
  <div className="border border-gray-300 rounded-lg mb-4 bg-white shadow-sm">
    <button
      onClick={onToggle}
      className={`w-full px-4 sm:px-6 py-3 sm:py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${isOpen ? 'bg-gray-50' : ''}`}
    >
      <h3 className="text-base sm:text-lg font-semibold text-gray-900 pr-2">{title}</h3>
      {isOpen ? (
        <ChevronDown className="h-4 w-4 sm:h-5 sm:w-5 text-[#ab3d22] flex-shrink-0" />
      ) : (
        <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 flex-shrink-0" />
      )}
    </button>
    {isOpen && (
      <div className="px-4 sm:px-6 pt-3 sm:pt-4 pb-3 sm:pb-4">
        <div className="text-gray-700 leading-relaxed text-sm sm:text-base">
          {children}
        </div>
      </div>
    )}
  </div>
);

const CalloutBox = ({ children, type = "info" }) => {
  const bgColor = type === "important" ? "bg-[#ab3d22]/10" : "bg-white";
  const borderColor = type === "important" ? "border-[#ab3d22]/30" : "border-gray-300";
  
  return (
    <div className={`${bgColor} ${borderColor} border rounded-lg p-3 sm:p-4 my-4 sm:my-6 shadow-sm`}>
      {children}
    </div>
  );
};

const AdvertisePage = () => {
  const [openSections, setOpenSections] = useState({
    whyAdvertise: true,
    ourAudience: false,
    editorialIntegrity: false,
    opportunities: false,
    commitment: false,
    contact: false,
  });

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="min-h-screen bg-[#f8f8f8]">
      <Navbar />

      {/* Editorial Hero Banner */}
      <section className="relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 bg-[#d6dbd8]"></div>
        
        {/* Subtle Glow Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-radial from-slate-200/20 to-transparent rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/3 right-1/3 w-80 h-80 bg-gradient-radial from-zinc-200/20 to-transparent rounded-full blur-3xl"></div>
        </div>
        
        <Container className="relative py-8 sm:py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:min-h-[200px] w-full">
            <div className="text-left flex-1 max-w-lg">
              {/* Main Headline */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 relative" style={{fontFamily: 'Poppins, sans-serif', fontWeight: 700}}>
                Advertise With Us
                {/* Brand Accent Line */}
                <div className="absolute -bottom-2 left-0 w-24 h-1 bg-[#ab3d22] rounded-full"></div>
              </h1>
              
              {/* Refined Tagline */}
              <p className="text-sm md:text-base text-gray-500 leading-relaxed font-light">
                Reach beauty and wellness enthusiasts who trust Radiance for clarity and honest guidance.
              </p>
            </div>
            
            {/* CTA Button */}
            <div className="flex justify-start md:justify-end w-full md:w-auto md:flex-shrink-0 items-end">
              <a 
                href="mailto:hello@meetradiance.com" 
                className="inline-flex items-center justify-center bg-[#ab3d22] hover:bg-[#8a2f1a] text-white font-medium px-6 py-3 rounded-lg text-sm transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 w-full md:w-auto"
              >
                Get Started Today
                <Mail className="ml-2 h-4 w-4" />
              </a>
            </div>
          </div>
        </Container>
      </section>

      <Container className="py-8 sm:py-10">
        <div className="max-w-4xl mx-auto">
          {/* Last Updated */}
          <div className="flex items-center gap-2 mb-8 text-sm text-gray-500">
            <Calendar className="h-4 w-4" />
            <span>Last Updated: December 8, 2025</span>
          </div>

          {/* Why Advertise With Radiance? */}
          <AccordionItem
            title="Why Advertise With Radiance?"
            isOpen={openSections.whyAdvertise}
            onToggle={() => toggleSection('whyAdvertise')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance is an emerging leader in beauty and wellness discovery — a place where readers come to cut through the noise and find products that genuinely work for them.
              </p>
              <p className="text-sm text-gray-600">
                Our audience is highly engaged, research-driven, and values transparency. Brands partner with Radiance to connect with consumers who appreciate authenticity and thoughtful curation.
              </p>
              <p className="text-sm text-gray-600">
                If your products deliver real results and align with what beauty shoppers truly care about, Radiance is where they belong.
              </p>
            </div>
          </AccordionItem>

          {/* Our Audience */}
          <AccordionItem
            title="Our Audience"
            isOpen={openSections.ourAudience}
            onToggle={() => toggleSection('ourAudience')}
          >
            <div className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Users className="h-4 w-4 text-[#ab3d22]" />
                  Beauty & Wellness Enthusiasts
                </h4>
                <p className="text-sm text-gray-600">
                  Our readers are curious, informed consumers who want clarity — not hype. They actively seek recommendations they can trust.
                </p>
              </div>
              
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900">Their Interests Include:</h4>
                <ul className="text-sm text-gray-600 space-y-1 ml-4">
                  <li>• Skincare, makeup, haircare, and beauty routines</li>
                  <li>• Wellness, self-care, and lifestyle improvement</li>
                  <li>• Learning from community discussions and real user experiences</li>
                  <li>• Research-driven purchasing</li>
                  <li>• Following beauty trends and new product launches</li>
                  <li>• Frequent online shopping across major retailers</li>
                </ul>
              </div>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Lightbulb className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Engagement that Matters</p>
                    <p className="text-sm">
                      Radiance readers don't just browse — they act. They explore, save, share, and purchase based on recommendations they feel confident in.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Editorial Integrity */}
          <AccordionItem
            title="Editorial Integrity"
            isOpen={openSections.editorialIntegrity}
            onToggle={() => toggleSection('editorialIntegrity')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Advertisers are partners, not influencers of our editorial decisions.
              </p>
              
              <p className="text-sm text-gray-600 font-medium">
                All sponsored opportunities are:
              </p>
              
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• Clearly labeled ("Sponsored" or "In Partnership With…")</li>
                <li>• Produced with full editorial oversight</li>
                <li>• Aligned with Radiance's values and community interests</li>
                <li>• Designed to add value, not noise</li>
              </ul>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Independent Editorial</p>
                    <p className="text-sm">
                      Radiance's product recommendations, rankings, and editorial content remain fully independent.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Opportunities to Partner */}
          <AccordionItem
            title="Opportunities to Partner"
            isOpen={openSections.opportunities}
            onToggle={() => toggleSection('opportunities')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                We offer limited and highly curated partnership formats, including:
              </p>
              
              <ul className="text-sm text-gray-600 space-y-1 ml-4">
                <li>• Sponsored articles</li>
                <li>• Brand features</li>
                <li>• Product spotlights</li>
                <li>• Category or routine-focused collaborations</li>
                <li>• Custom editorial integrations</li>
              </ul>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Quality Standard</p>
                    <p className="text-sm">
                      Everything we create is held to the same standard as our editorial content — thoughtful, transparent, and genuinely useful to readers.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Our Commitment to Your Brand */}
          <AccordionItem
            title="Our Commitment to Your Brand"
            isOpen={openSections.commitment}
            onToggle={() => toggleSection('commitment')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Radiance partnerships are designed to feel premium, intentional, and authentic. We work with brands whose values resonate with our audience and whose products deliver real results.
              </p>
              
              <CalloutBox type="important">
                <div className="flex items-start gap-3">
                  <Heart className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Brand Respect</p>
                    <p className="text-sm">
                      Your brand will be presented in a way that respects both your identity and our readers' trust.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>

          {/* Contact */}
          <AccordionItem
            title="Contact Us"
            isOpen={openSections.contact}
            onToggle={() => toggleSection('contact')}
          >
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Ready to connect with Radiance's trusted, engaged audience? We'd love to discuss how we can support your campaign goals.
              </p>
              
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-[#ab3d22]" />
                    <span className="text-sm font-medium">📩 Advertising & Partnership Inquiries:</span>
                    <a href="mailto:hello@meetradiance.com" className="text-sm text-[#ab3d22] hover:underline">
                      hello@meetradiance.com
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-[#ab3d22]" />
                    <span className="text-sm font-medium">⏱️ Response Time:</span>
                    <span className="text-sm text-gray-600">Within 48 hours</span>
                  </div>
                </div>
              </div>
              
              <CalloutBox>
                <div className="flex items-start gap-3">
                  <Handshake className="h-5 w-5 text-[#ab3d22] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-gray-900 mb-1">Please Include</p>
                    <p className="text-sm">
                      Please include your brand information, campaign goals, and budget range so we can create the most relevant proposal.
                    </p>
                  </div>
                </div>
              </CalloutBox>
            </div>
          </AccordionItem>
        </div>
      </Container>

      <Footer />
    </div>
  );
};

export default AdvertisePage;
